import { ReportEvaluation, ReportFinding, ReportValues, RunReportContext, SignoffSpec } from './types';
import { RunSnapshot, RunTraceRequest } from './runs';
import { BlockDefinition, BlockInput } from './blocks';
import { Bindings, Requirement, ResolvedBinding, bindingsField, resolveBindings } from './bindings';
import { Dimension, dimensionsField, resolveDimensions } from './dimensions';
import { LayoutBlock, LayoutSection, ReportLayout, layoutField } from './layout';
import { PlacedRun, placeRun, sectionMapField } from './placement';
import { Fields, defineValues, v } from './schema';
import { STANDARD_BLOCKS } from './standardBlocks';
import { VerdictLabels, finding, verdictFrom } from './verdict';
import { TableRow } from './types';

/**
 * Composed reports: a run report whose document is a layout of blocks. The
 * report's code declares the layout, any settings and values of its own, and
 * its report-level checks; the blocks do the rest, including their share of
 * the verdict.
 */

export interface CompositionSettings extends ReportValues {
  layout: ReportLayout;
  sectionMap: TableRow[];
  dimensions: Dimension[];
  bindings: Bindings;
}

export interface ComposedBlock {
  block: LayoutBlock;
  /** undefined for a block type this app doesn't know (a bundle's block, removed since) */
  definition: BlockDefinition | undefined;
  input: BlockInput;
}

export interface Composition {
  context: RunReportContext;
  run: RunSnapshot;
  values: ReportValues;
  settings: CompositionSettings;
  layout: ReportLayout;
  placed: PlacedRun;
  bindings: Record<string, ResolvedBinding>;
  dimensions: Dimension[];
  sections: Array<{ section: LayoutSection; blocks: ComposedBlock[] }>;
}

export type BlockRegistry = Map<string, BlockDefinition>;

export function blockRegistry(own: BlockDefinition[] = []): BlockRegistry {
  return new Map([...STANDARD_BLOCKS, ...own].map((b) => [b.type, b as BlockDefinition]));
}

export function compose(args: { context: RunReportContext; values: ReportValues; settings: CompositionSettings; requires?: Record<string, Requirement>; registry: BlockRegistry }): Composition {
  const { context, values, settings, registry } = args;
  const run = context.run;
  const placed = placeRun(run, settings.sectionMap);
  const bindings = resolveBindings(args.requires, run, settings.bindings);
  const dimensions = resolveDimensions(settings.dimensions, run);
  const base = { context, run, settings, values, placed, bindings, dimensions };
  const sections = settings.layout.sections.map((section) => ({
    section,
    blocks: section.blocks.map((block): ComposedBlock => {
      const definition = registry.get(block.type);
      const props = definition ? (definition.props.normalize(block.props) as ReportValues) : block.props;
      return { block, definition, input: { ...base, block, props } };
    }),
  }));
  return { ...base, layout: settings.layout, sections };
}

/** Every check the layout's blocks make, in print order. */
export function compositionFindings(c: Composition): ReportFinding[] {
  return c.sections.flatMap((s) => s.blocks.flatMap((b) => (b.definition?.findings ? b.definition.findings(b.input) : [])));
}

/** One block of a composition by its id in the layout: what a document reads to print a block's model elsewhere, such as a summary tile. */
export function composedBlock(c: Composition, id: string): ComposedBlock | undefined {
  for (const s of c.sections) {
    const found = s.blocks.find((b) => b.block.id === id);
    if (found) return found;
  }
  return undefined;
}

/**
 * What a composed report asks the server to read: its own requests, then each
 * block's. A file a block draws is not drawn again plainly, so a report's own
 * request for a file a block asks for is dropped.
 */
export function compositionTraces(c: Composition, own: RunTraceRequest[]): RunTraceRequest[] {
  const asked = c.sections.flatMap((s) => s.blocks.flatMap((b) => (b.definition?.traces ? b.definition.traces(b.input) : [])));
  const drawn = new Set(asked.map((r) => r.fileId));
  return [...own.filter((r) => !drawn.has(r.fileId)), ...asked];
}


function isEmpty(b: ComposedBlock): boolean {
  return b.definition?.isEmpty ? b.definition.isEmpty(b.input) : false;
}

/**
 * What prints: blocks set to leave out when empty are dropped when they have
 * nothing to show, and a section left with no blocks goes with them — except
 * while the report is being filled in, when every field must stay reachable.
 */
export function printedSections(c: Composition, options: { editing: boolean }): Composition['sections'] {
  return c.sections
    .map((s) => ({ section: s.section, blocks: s.blocks.filter((b) => (options.editing && b.definition?.fields) || b.block.whenEmpty !== 'leave-out' || !isEmpty(b)) }))
    .filter((s) => s.blocks.length > 0 || c.sections.find((x) => x.section.id === s.section.id)?.blocks.length === 0);
}

// ── defining a composed report ──────────────────────────────────────────────

export interface ComposedReportSpec<VF extends Fields, SF extends Fields> {
  key: string;
  title: string;
  description: string;
  version: string;
  defaultLayout: () => ReportLayout;
  /** where a procedure's sections go, for procedures that name them otherwise (placement.ts) */
  sectionMap?: TableRow[];
  /** the test matrix: what the procedure repeats its steps over (dimensions.ts) */
  dimensions?: Dimension[];
  /** parameters the report names for its requirements, for procedures that name them otherwise (bindings.ts) */
  bindings?: Bindings;
  /** settings of the report's own, beside layout, section map, dimensions and bindings */
  settings?: SF;
  /** per-run values of the report's own, beside the blocks' fields */
  values?: VF;
  requires?: Record<string, Requirement>;
  signoffs?: SignoffSpec[];
  /** the report's own block types, beside the SDK's */
  blocks?: BlockDefinition[];
  traces?: (run: RunSnapshot) => RunTraceRequest[];
  /** checks the report makes itself, beside its blocks' */
  findings?: (c: Composition) => ReportFinding[];
  /** the words the verdict prints; a function to word it from the run (NOT COMPLETE, ABORTED) */
  labels?: VerdictLabels | ((c: Composition) => VerdictLabels);
  /** bring settings up to date from values an older version stored per run, before the report reads them */
  adapt?: (settings: CompositionSettings, values: ReportValues, context: RunReportContext) => CompositionSettings;
  /** bring values an older version stored up to this shape, before the schema normalizes them */
  migrateValues?: (raw: Record<string, unknown>) => Record<string, unknown>;
}

export interface ComposedReportDefinition {
  key: string;
  subject: 'run';
  title: string;
  description: string;
  version: string;
  defaultValues: () => ReportValues;
  normalizeValues: (raw: unknown) => ReportValues;
  defaultSettings: () => ReportValues;
  normalizeSettings: (raw: unknown) => ReportValues;
  requires?: Record<string, Requirement>;
  signoffs?: SignoffSpec[];
  blocks?: BlockDefinition[];
  traces?: (run: RunSnapshot) => RunTraceRequest[];
  judge: (values: ReportValues, context: RunReportContext) => ReportEvaluation;
  evaluate: (values: ReportValues, context: RunReportContext) => ReportEvaluation;
  /** the composition behind a document: the same one the verdict was judged from */
  compose: (values: ReportValues, context: RunReportContext) => Composition;
}

export function defineComposedReport<VF extends Fields = Record<string, never>, SF extends Fields = Record<string, never>>(spec: ComposedReportSpec<VF, SF>): ComposedReportDefinition {
  const settingsSchema = defineValues({
    layout: layoutField(spec.defaultLayout, { help: 'The sections of the report and the blocks in each.' }),
    sectionMap: sectionMapField({ default: spec.sectionMap }),
    dimensions: dimensionsField({ default: spec.dimensions }),
    bindings: bindingsField({ help: 'Which procedure parameter plays each part the report reads.', default: spec.bindings }),
    ...(spec.settings ?? ({} as SF)),
  });
  const baseValues = defineValues({ fields: v.map(v.text(), { hidden: true }), signoffs: v.map(v.text(), { hidden: true }), ...(spec.values ?? ({} as VF)) }, { keepUnknown: true });
  const valuesSchema = {
    ...baseValues,
    normalize: (raw: unknown) => baseValues.normalize(spec.migrateValues && raw && typeof raw === 'object' ? spec.migrateValues(raw as Record<string, unknown>) : raw),
  };
  const registry = blockRegistry(spec.blocks);
  const settingsFor = (values: ReportValues, context: RunReportContext): CompositionSettings => {
    const settings = settingsSchema.normalize(context.settings ?? {}) as unknown as CompositionSettings;
    return spec.adapt ? spec.adapt(settings, values, context) : settings;
  };
  const composeFor = (values: ReportValues, context: RunReportContext) =>
    compose({ context, values: valuesSchema.normalize(values) as ReportValues, settings: settingsFor(values, context), requires: spec.requires, registry });
  const hasBlockTraces = Array.from(registry.values()).some((b) => typeof b.traces === 'function');
  const traces = spec.traces || hasBlockTraces
    ? (run: RunSnapshot) => {
      const own = spec.traces?.(run) ?? [];
      // settings are the author's, in code: what the blocks draw follows from the layout and the run alone
      const c = composeFor(valuesSchema.defaults() as ReportValues, { run, project: null, traces: {} });
      return compositionTraces(c, own);
    }
    : undefined;
  const judge = (values: ReportValues, context: RunReportContext): ReportEvaluation => {
    const c = composeFor(values, context);
    const unknown = c.sections.flatMap((s) => s.blocks.filter((b) => !b.definition)).map((b) => finding.note(`block-${b.block.id}`, `The layout has a “${b.block.type}” block this report cannot draw; it is left out.`));
    const labels = typeof spec.labels === 'function' ? spec.labels(c) : spec.labels;
    return verdictFrom([...(spec.findings?.(c) ?? []), ...compositionFindings(c), ...unknown], labels);
  };
  return {
    key: spec.key,
    subject: 'run',
    title: spec.title,
    description: spec.description,
    version: spec.version,
    defaultValues: () => valuesSchema.defaults() as ReportValues,
    normalizeValues: (raw) => valuesSchema.normalize(raw) as ReportValues,
    defaultSettings: () => settingsSchema.defaults() as ReportValues,
    normalizeSettings: (raw) => settingsSchema.normalize(raw) as ReportValues,
    requires: spec.requires,
    signoffs: spec.signoffs,
    blocks: spec.blocks,
    traces,
    judge,
    evaluate: judge,
    compose: composeFor,
  };
}

